import { useEffect } from 'react';
import { useVersionCheck } from './hooks/useVersionCheck';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeContextProvider } from './context/ThemeContext';
import api from './services/api';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import ExpenseManagement from './pages/ExpenseManagement';
import TodoApp from './pages/TodoApp';
import ContentList from './pages/ContentList';
import ContentForm from './pages/ContentForm';
import BulkUpload from './pages/BulkUpload';
import Settings from './pages/Settings';
import SocialMediaMonitor from './pages/SocialMediaMonitor';
import UserManagement from './pages/UserManagement';
import FinanceHub from './pages/FinanceHub';
import EmployeeDashboard from './pages/EmployeeDashboard';
import DirectorDashboard from './pages/DirectorDashboard';
import EditorDashboard from './pages/EditorDashboard';
import MySchedule from './pages/MySchedule';
import ManagerDashboard from './pages/ManagerDashboard';
import ContactDiary from './pages/ContactDiary';
import AdManagement from './pages/AdManagement';
import Awards from './pages/Awards';
import AttendanceManagement from './pages/AttendanceManagement';
import InventoryManagement from './pages/InventoryManagement';
import PropertyManagement from './pages/PropertyManagement';
import AccessControl from './pages/AccessControl';
import RecruitmentManagement from './pages/RecruitmentManagement';
import OnboardingManagement from './pages/OnboardingManagement';
import Meetings from './pages/Meetings';
import CompanyLeaveCalendar from './pages/CompanyLeaveCalendar';
import ShootPlanner from './pages/ShootPlanner';
import ShootCalendar from './pages/ShootCalendar';
import ProfitDistributionManagement from './pages/ProfitDistributionManagement';
import EmployeeSalary from './pages/EmployeeSalary';
import Notifications from './pages/Notifications';
import ContentAuditLog from './pages/ContentAuditLog';
import UserActivityLog from './pages/UserActivityLog';
import './styles/App.css';

// Protected Route component
function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, user, loading, permsLoaded, canViewPath } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // RBAC (authoritative when the matrix has loaded): the Access Control matrix
  // decides page access. Wait for it to load to avoid a flash-redirect.
  if (permsLoaded) {
    const allowed = canViewPath(location.pathname); // true / false / null(unknown)
    if (allowed === true) return children;
    if (allowed === false) return <Navigate to="/" replace />;
    // allowed === null → path not in the matrix; fall through to legacy roles.
  }

  // Legacy fallback: role-based access (primary role + roles array).
  if (allowedRoles) {
    if (allowedRoles.includes(user.role)) return children;
    let userRoles = [];
    if (Array.isArray(user?.roles)) userRoles = user.roles;
    else if (typeof user?.roles === 'string') { try { userRoles = JSON.parse(user.roles); } catch (e) { userRoles = []; } }
    if (!userRoles.some((role) => allowedRoles.includes(role))) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
}

const PAGE_LABELS = {
  '/': 'Dashboard', '/content': 'Content Library', '/content/new': 'Add Content',
  '/content/audit': 'Content Audit Log', '/user-activity': 'User Activity Log',
  '/bulk-upload': 'Bulk Upload', '/social-media': 'Social Media', '/users': 'User Management',
  '/settings': 'Configure', '/manager': 'Manager Dashboard', '/employee': 'Employee Dashboard',
  '/director': 'Director Dashboard', '/editor': 'Editor Dashboard',
  '/expenses': 'Expense Management', '/todos': 'Todo App', '/awards': 'Awards & Recognition',
  '/attendance': 'Attendance', '/inventory': 'Inventory Management', '/property': 'Property Management', '/access-control': 'Access Control',
  '/recruitment': 'Recruitment', '/onboarding': 'Onboarding', '/leave-calendar': 'Leave Calendar',
  '/profit-distribution': 'Profit Distribution', '/salary': 'Employee Salary',
  '/notifications': 'Notifications', '/finance-hub': 'Finance Hub',
  '/onboarding': 'Onboarding', '/meetings': 'Meetings',
};

const STORAGE_KEY = 'jbde_page_activity';
const FLUSH_INTERVAL = 30 * 60 * 1000; // 30 minutes

function getLabel(path) {
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith('/content/edit/')) return 'Edit Content';
  return path;
}

function loadActivity() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}

function saveActivity(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

function PageTracker() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const flush = async (activity) => {
    if (!activity || !activity.pages || !Object.keys(activity.pages).length) return;
    const pages = Object.entries(activity.pages).map(([page, { count, lastTime }]) => ({
      page: getLabel(page), count, time: lastTime,
    }));
    try {
      await api.post('/users/page-batch', { date: activity.date, pages });
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  // Track navigation
  useEffect(() => {
    if (!isAuthenticated || location.pathname === '/login') return;

    const today = new Date().toISOString().split('T')[0];
    const now   = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    let activity = loadActivity();

    // Day boundary — flush previous day's data and start fresh
    if (activity && activity.date !== today) {
      flush(activity);
      activity = null;
    }

    if (!activity) activity = { date: today, pages: {} };

    const path = location.pathname;
    if (!activity.pages[path]) activity.pages[path] = { count: 0, lastTime: now };
    activity.pages[path].count += 1;
    activity.pages[path].lastTime = now;

    saveActivity(activity);
  }, [location.pathname, isAuthenticated]);

  // Start 30-min flush timer
  useEffect(() => {
    if (!isAuthenticated) return;
    const timer = setInterval(() => {
      const activity = loadActivity();
      if (activity) flush(activity);
    }, FLUSH_INTERVAL);
    return () => clearInterval(timer);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flush on page unload (browser close / refresh)
  useEffect(() => {
    const handleUnload = () => {
      const activity = loadActivity();
      if (activity) {
        const pages = Object.entries(activity.pages).map(([page, { count, lastTime }]) => ({
          page: getLabel(page), count, time: lastTime,
        }));
        // Use sendBeacon for reliability on page unload
        navigator.sendBeacon
          ? navigator.sendBeacon(
              `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1'}/users/page-batch`,
              new Blob([JSON.stringify({ date: activity.date, pages })], { type: 'application/json' })
            )
          : api.post('/users/page-batch', { date: activity.date, pages }).catch(() => {});
        localStorage.removeItem(STORAGE_KEY);
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  return null;
}

// App Routes component (needs to be inside Router)
function AppRoutes() {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <PageTracker />
      <Routes>
        {/* Admin/Manager only routes */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'lead']}>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/content" 
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'lead']}>
              <ContentList />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/content/new"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'director', 'lead']}>
              <ContentForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content/edit/:id"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'director', 'lead']}>
              <ContentForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content/audit"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <ContentAuditLog />
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-activity"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <UserActivityLog />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/bulk-upload" 
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <BulkUpload />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/social-media"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager']}>
              <SocialMediaMonitor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contacts"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager']}>
              <ContactDiary />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ads"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager']}>
              <AdManagement />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/users"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr']}>
              <UserManagement />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager']}>
              <Settings />
            </ProtectedRoute>
          } 
        />

        {/* Manager Dashboard - Owner, Manager, and HR */}
        <Route 
          path="/manager" 
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr', 'lead']}>
              <ManagerDashboard />
            </ProtectedRoute>
          } 
        />

        {/* Everyone can access Employee Dashboard */}
        <Route
          path="/employee"
          element={
            <ProtectedRoute>
              <EmployeeDashboard />
            </ProtectedRoute>
          }
        />

        {/* My Schedule — all logged-in users */}
        <Route
          path="/my-schedule"
          element={
            <ProtectedRoute>
              <MySchedule />
            </ProtectedRoute>
          }
        />

        {/* Director only */}
        <Route 
          path="/director" 
          element={
            <ProtectedRoute allowedRoles={['director']}>
              <DirectorDashboard />
            </ProtectedRoute>
          } 
        />

        {/* Editor only */}
        <Route 
          path="/editor" 
          element={
            <ProtectedRoute allowedRoles={['editor']}>
              <EditorDashboard />
            </ProtectedRoute>
          } 
        />

        {/* Expense Management - Owner, Finance, Manager, HR */}
        <Route
          path="/expenses"
          element={
            <ProtectedRoute allowedRoles={['owner', 'finance', 'manager', 'hr']}>
              <ExpenseManagement />
            </ProtectedRoute>
          }
        />

        {/* Todo App - Everyone can access */}
        <Route 
          path="/todos" 
          element={
            <ProtectedRoute>
              <TodoApp />
            </ProtectedRoute>
          } 
        />

        {/* Awards - Manager, Owner, and HR can give awards; Everyone can view */}
        <Route 
          path="/awards" 
          element={
            <ProtectedRoute>
              <Awards />
            </ProtectedRoute>
          } 
        />

        {/* Attendance Management - HR, Manager, Owner, Lead */}
        <Route
          path="/attendance"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr', 'lead']}>
              <AttendanceManagement />
            </ProtectedRoute>
          } 
        />

        {/* Inventory Management - HR, Manager, Owner, Lead */}
        <Route
          path="/inventory"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr', 'lead']}>
              <InventoryManagement />
            </ProtectedRoute>
          }
        />

        {/* Property Management - HR, Manager, Owner, Lead */}
        <Route
          path="/property"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr', 'lead']}>
              <PropertyManagement />
            </ProtectedRoute>
          }
        />

        {/* Access Control - Owner only */}
        <Route
          path="/access-control"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <AccessControl />
            </ProtectedRoute>
          }
        />

        {/* Recruitment Management - HR, Manager, Owner, Lead */}
        <Route
          path="/recruitment"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr', 'lead']}>
              <RecruitmentManagement />
            </ProtectedRoute>
          } 
        />

        {/* Meetings - all authenticated users */}
        <Route
          path="/meetings"
          element={
            <ProtectedRoute>
              <Meetings />
            </ProtectedRoute>
          }
        />

        {/* Employee Onboarding - HR, Manager, Owner, Lead */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr', 'lead']}>
              <OnboardingManagement />
            </ProtectedRoute>
          }
        />

        {/* Company Leave Calendar - HR, Manager, and Owner only */}
        <Route 
          path="/leave-calendar"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr']}>
              <CompanyLeaveCalendar />
            </ProtectedRoute>
          }
        />

        {/* Shoot Planner + Calendar */}
        <Route
          path="/shoot-planner"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'lead', 'director']}>
              <ShootPlanner />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shoot-calendar"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'lead', 'director']}>
              <ShootCalendar />
            </ProtectedRoute>
          }
        />

        {/* Profit Distribution Management - Owner only */}
        <Route
          path="/profit-distribution"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <ProfitDistributionManagement />
            </ProtectedRoute>
          }
        />

        {/* Employee Salary - Owner, Manager, HR */}
        <Route
          path="/salary"
          element={
            <ProtectedRoute allowedRoles={['owner', 'manager', 'hr']}>
              <EmployeeSalary />
            </ProtectedRoute>
          }
        />

        {/* Finance Hub - Owner only */}
        <Route
          path="/finance-hub"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <FinanceHub />
            </ProtectedRoute>
          }
        />

        {/* Notifications - Owner only */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute allowedRoles={['owner']}>
              <Notifications />
            </ProtectedRoute>
          }
        />

        {/* Redirect to login for unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function UpdateBanner() {
  useVersionCheck();
  return null;
}

function App() {
  return (
    <ThemeContextProvider>
      <AuthProvider>
        <Router>
          <UpdateBanner />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeContextProvider>
  );
}

export default App;
